import { GetInterviewCandidateListType } from '../../../types/Recruitment/CandidateList/CandidateInterviewListTypes'
import {
  country,
  CandidateAppliedForList,
  CandidateSourceType,
  CandidateJobTypeEnum,
  viewHandlerProps,
} from '../../../types/Recruitment/CandidateList/CandidateListTypes'
import { GetAllTechnology } from '../../../types/Recruitment/JobOpenings/jobOpeningsTypes'
import { getJobCode } from '../../../types/Recruitment/InterviewerConfiguration/interviewerConfigurationTypes'

export const maxExperience = 5
export const initialPage = 1
export const customPageSize = 20

export const initialCandidateCountry: country = {
  countryCode: '',
  id: -1,
  name: '',
  mobileCode: '',
}

export const candidateFeatureId = 126
export const onlyAlphabetsRegex = /^[a-zA-Z\s]+$/
export const acceptEmailIdRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/

export const initialCandidateAppliedForList: CandidateAppliedForList = {
  id: -1,
  jobCode: '',
  positionVacant: '',
  minimumExperience: '',
  description: '',
  opendDate: '',
  expiryDate: null,
  noOfRequirements: -1,
  offered: -1,
  remaining: -1,
  status: '',
}

//* an exportable const is important for initial values to be used across the component
export const initialCandidateInterviewFilterValues: GetInterviewCandidateListType =
  {
    selectionStatus: 'Current Month',
    fromDate: '',
    toDate: '',
    startIndex: initialPage,
    endIndex: customPageSize,
    searchByCandidateName: '',
    searchByRecruiterName: '',
    searchBySourceName: '',
    searchByMultipleFlag: '',
    candidateStatus: 'ALL',
    selectionTechnology: '',
    searchByExperience: '',
    selectionCountry: '',
    recruiter: '',
    experience: '',
    interviewRound: '',
    isResumeMatcher: '',
    isExternalCreated: 'All',
  }

export const initialCandidateFilterValues: viewHandlerProps = {
  startIndex: initialPage,
  endIndex: customPageSize,
  candidateStatus: '',
  selectionTechnology: '',
  selectionCountry: '',
  recruiter: '',
  experience: '',
  searchByMultipleFlag: '',
  fromDate: '',
  toDate: '',
  selectionStatus: '',
  interviewRound: '',
  isResumeMatcher: '',
  isExternalCreated: 'All',
  appliedFor: '',
}

export const emailExistsError = 'Email already exists'
export const mobileExistsError = 'Mobile Number already exists'

export const candidateStatusMap: { [key: string]: string } = {
  ALL: 'All',
  OFFERED: 'Offered',
  OFFER_CANCELLED: 'Offer Cancelled',
  REJECTED: 'Rejected',
  NEW: 'New',
  IN_PROCESS: 'In Progress',
  RESCHEDULED: 'Rescheduled',
  REPROCESS: 'ReProcess',
  HOLD: 'Hold',
  NO_SHOW: 'No show',
  DID_NOT_JOIN: 'Did not Join',
  JOINED: 'Joined',
  L1_SHORTLISTED: 'L1_SHORTLISTED',
  L2_SHORTLISTED: 'L2_SHORTLISTED',
  L3_SHORTLISTED: 'L3_SHORTLISTED',
}

export const initialGetAllTechnology: GetAllTechnology = {
  id: -1,
  name: '',
}

export const candidateSourceTypeList: CandidateSourceType[] = [
  CandidateSourceType.internal,
  CandidateSourceType.external,
  CandidateSourceType.Others,
]

export const candidateJobTypeList: CandidateJobTypeEnum[] = [
  CandidateJobTypeEnum.fullTime,
  CandidateJobTypeEnum.partTime,
]

export const filterCandidateCountryByCountryId = (
  list: country[],
  id: number,
): country => {
  const filteredOne = list.find((currCountry) => currCountry.id === id)
  if (filteredOne === undefined) {
    return initialCandidateCountry
  }
  return filteredOne
}

export const filterCandidateAppliedForById = (
  list: CandidateAppliedForList[],
  selectedId: number,
): CandidateAppliedForList => {
  const filteredOne = list.find((current) => current.id === selectedId)
  if (filteredOne === undefined) {
    return initialCandidateAppliedForList
  }
  return filteredOne
}

// Helper function to filter job codes and convert to CandidateAppliedForList format
export const filterJobCodeByIdAndConvert = (
  list: getJobCode[],
  selectedId: number,
): CandidateAppliedForList => {
  const filteredOne = list.find((current) => current.id === selectedId)
  if (filteredOne === undefined) {
    return initialCandidateAppliedForList
  }
  return {
    id: filteredOne.id,
    jobCode: filteredOne.jobCode,
    positionVacant: filteredOne.positionVacant,
    minimumExperience: '',
    description: '',
    opendDate: '',
    expiryDate: null,
    noOfRequirements: 0,
    offered: 0,
    remaining: 0,
    status: '',
  }
}

export const get18YearsBackDate = (): Date => {
  const todayDate = new Date()
  return new Date(
    todayDate.getFullYear() - 18,
    todayDate.getMonth(),
    todayDate.getDate(),
  )
}

export const isTechnologyInTheList = (
  technologyList: GetAllTechnology[],
  technologyName: string,
): boolean => {
  const filteredResult = technologyList.find(
    (technology) =>
      technology.name.toLowerCase() === technologyName.trim().toLowerCase(),
  )
  return filteredResult !== undefined // returns true if it is in the list
}

export const getFinalIncomingCandidateValue = (data: string | null): string => {
  return data === null ? '' : data
}

export const nonRequiredFinalCandidateData = (data: string): string | null => {
  return data?.trim() === '' ? null : data
}

export const getTrimmedCandidateValue = (data: string): string => {
  return data?.trim()
}

// returns the error
export const getFileValidation = (file: File | undefined): string => {
  const typesAccepted = ['pdf', 'doc', 'docx', 'DOCX']
  if (file === undefined) {
    return ''
  }
  const fileExtension = file.name.split('.').pop() as string
  if (!typesAccepted.includes(fileExtension)) {
    return 'Please choose doc or docx or pdf file'
  }
  if (file.size > 2048000) {
    return 'Please upload a file less than 2MB.'
  }
  return ''
}

export const getCurrentScheduleTime = (): string => {
  const currentDate = new Date()
  let hours = currentDate.getHours()
  const minutes = currentDate.getMinutes()
  let amOrPm = 'AM'

  // Convert to 12-hour format
  if (hours > 12) {
    hours -= 12
    amOrPm = 'PM'
  }

  // Add leading zeros if necessary
  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')

  return `${formattedHours}:${formattedMinutes} ${amOrPm}`
}

export const getLabelAsterixDataTestId = (label: string): Array<string> => {
  return [`addCand-${label}`, `addCand-${label}-ast`] // [testid for label, testid for asterix]
}

export const getDataInputTestId = (id: string): string => {
  return `addCandInp-${id}`
}
export interface CandidateFilterBody {
  selectionStatus?: string
  fromDate?: string
  toDate?: string
  searchByMultipleFlag?: string
  candidateStatus?: string
  selectionTechnology?: string
  experience?: string
  selectionCountry?: number | string
  recruiter?: string
  interviewRound?: string
  isResumeMatcher?: string
  isExternalCreated?: string
}
export const withDefault = <T>(value: T | undefined | null, fallback: T): T =>
  value ?? fallback
